import { gateIsOpen } from './power';
/**
 * Surge: every shut powered gate opens until a block gets out through one.
 *
 * A board MODE in the exact shape of Rainbow (`model/rainbow.ts`): the
 * `moveCount` at which it was switched on is the whole state (present means
 * active), and it lives on the per-attempt board, so a restart or a jump drops
 * it and undo never restores it.
 *
 * ## How it ends (Module 74, D3)
 *
 * Module 63 ended it on the next committed move, exit or not ("for one move").
 * Unless the powered-gate block was already lined up at its shut gate, that
 * spent a paid Surge on the move that got the block into position. The player
 * had to time the purchase to the exact move, and a wrong guess bought nothing.
 * Reported from play: "player can feel cheated".
 *
 * It now ends when a block leaves through a gate **that only Surge was holding
 * open**: {@link surgeSpentBy}. Three readings were rejected:
 * - *any exit* (Rainbow's literal rule) lets an unrelated block, leaving
 *   through an ordinary gate, spend it. That is the same complaint in a
 *   different form;
 * - *any powered gate* spends it on a gate whose plate is already covered,
 *   where Surge changed nothing;
 * - *N moves* is still a timing puzzle, with a counter to watch.
 *
 * Undo does not bring a spent Surge back (Rainbow's rule, unchanged). An undo
 * of a move that did NOT spend it leaves it open, because nothing was spent.
 *
 * Rainbow lifts colour; Surge lifts power. Never both (Module 63 decision 6).
 * The two options are separate fields on `ExitOptions` and nothing here reads
 * the other.
 */
export function isSurgeActive(state) {
    return state.surgeSinceMove !== undefined;
}
export function activateSurge(state) {
    return { ...state, surgeSinceMove: state.moveCount };
}
export function endSurge(state) {
    if (!isSurgeActive(state))
        return state;
    const next = { ...state };
    delete next.surgeSinceMove;
    return next;
}
/** How many moves the surge has stayed open (analytics). Never negative after an undo. */
export function surgeMovesOpen(state) {
    if (state.surgeSinceMove === undefined)
        return 0;
    return Math.max(0, state.moveCount - state.surgeSinceMove);
}
/**
 * Did a block leaving through `gate` spend the surge? True only when the exit
 * was decided under surge AND the gate would have refused without it: powered,
 * and its plate dark.
 *
 * `opts.poweredPlateIds` must be the power set AT THE EXIT INSTANT, the mover
 * included at the position it leaves from. That is the solver's rule
 * (`free-solver.ts`: "a block standing on its own gate's plate really does open
 * it") and the renderer's (`currentPowered()` mid-drag). Found in WP-74.5's
 * play-through: Beginner puzzle 30's plate sits right under its gate, so the
 * block leaving covers it, and a pre-move power set called that exit a spent
 * surge while the board called it plate-lit. Both move paths call this and only
 * this; the plugin's passes `applyFreeMove`'s `powered`, the set its exit was
 * decided with (Module 75).
 */
export function surgeSpentBy(gate, opts) {
    if (!opts.surge || !gate)
        return false;
    return !gateIsOpen(gate, opts.poweredPlateIds ?? NOTHING_POWERED);
}
const NOTHING_POWERED = new Set();
